import React, { useState, useEffect } from 'react';
import { ElectricityPrice, PriceArea } from '../types';
import { format, parseISO } from 'date-fns';
import { nb } from 'date-fns/locale';
import { Zap, Filter, ArrowUpDown } from 'lucide-react';
import { calculateElectricitySubsidy } from '../utils/subsidyCalculator';

interface PriceListProps {
  prices: ElectricityPrice[];
  loading: boolean;
  error: string | null;
  area?: PriceArea;
}

type SortOrder = 'time' | 'price-asc' | 'price-desc';

const PriceList: React.FC<PriceListProps> = ({ prices, loading, error, area }) => {
  const [sortOrder, setSortOrder] = useState<SortOrder>('time');
  const [showSubsidy, setShowSubsidy] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full text-center py-8">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (prices.length === 0) {
    return (
      <div className="w-full text-center py-8">
        <p>Ingen prisdata tilgjengelig for valgt dato og område.</p>
      </div>
    );
  }

  // Find the current hour
  const now = new Date();
  const currentHourIndex = prices.findIndex(
    (price) => 
      parseISO(price.time_start) <= now && 
      parseISO(price.time_end) > now
  );

  // Calculate average price
  const avgPrice = prices.reduce((sum, price) => sum + price.NOK_per_kWh, 0) / prices.length;

  // Sort prices based on selected order
  const sortedPrices = [...prices].sort((a, b) => {
    if (sortOrder === 'time') {
      return parseISO(a.time_start).getTime() - parseISO(b.time_start).getTime();
    } else if (sortOrder === 'price-asc') {
      return a.NOK_per_kWh - b.NOK_per_kWh;
    } else {
      return b.NOK_per_kWh - a.NOK_per_kWh;
    }
  });

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
        <div className="mb-3 sm:mb-0">
          <h3 className="text-lg font-medium">Timepriser</h3>
          <p className="text-sm text-gray-500">Alle priser for valgt dag</p>
        </div>
        
        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <button 
            onClick={() => setShowSubsidy(!showSubsidy)}
            className={`flex-1 sm:flex-initial text-xs px-3 py-1.5 rounded-full flex items-center justify-center ${
              showSubsidy 
                ? 'bg-green-100 text-green-800 border border-green-200'
                : 'bg-gray-100 text-gray-700 border border-gray-200'
            }`}
          >
            <ArrowUpDown className="w-3 h-3 mr-1" />
            {showSubsidy ? 'Skjul støtte' : 'Vis støtte'}
          </button>
          
          <div className="relative inline-block flex-1 sm:flex-initial">
            <div className="flex items-center bg-gray-100 rounded-md p-1 text-sm w-full">
              <Filter className="h-3 w-3 ml-1 text-gray-500" />
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOrder)}
                className="bg-transparent pr-8 pl-1 py-1.5 appearance-none focus:outline-none text-xs w-full"
              >
                <option value="time">Sorter etter tid</option>
                <option value="price-asc">Laveste pris først</option>
                <option value="price-desc">Høyeste pris først</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {isMobile ? (
        /* Mobile optimized card layout */
        <div className="space-y-3">
          {sortedPrices.map((price, index) => {
            const startTime = parseISO(price.time_start);
            const formattedTime = format(startTime, 'HH:mm', { locale: nb });
            const isCurrentHour = price.time_start === prices[currentHourIndex]?.time_start;
            const priceDiff = ((price.NOK_per_kWh - avgPrice) / avgPrice) * 100;
            const subsidyInfo = calculateElectricitySubsidy(price.NOK_per_kWh, area);
            
            // Determine background color based on price comparison
            let cardBgClass = '';
            let priceColorClass = '';
            
            if (price.NOK_per_kWh < avgPrice * 0.8) {
              cardBgClass = 'bg-green-50 border-green-200';
              priceColorClass = 'text-green-700 font-medium';
            } else if (price.NOK_per_kWh > avgPrice * 1.2) {
              cardBgClass = 'bg-red-50 border-red-200';
              priceColorClass = 'text-red-700 font-medium';
            } else {
              cardBgClass = isCurrentHour ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200';
              priceColorClass = 'text-gray-900';
            }
            
            if (isCurrentHour) {
              cardBgClass = 'bg-blue-50 border-blue-200';
            }
            
            return (
              <div key={price.time_start} className={`p-3 rounded-lg border ${cardBgClass}`}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    {isCurrentHour && (
                      <div className="bg-blue-100 p-1 rounded-full mr-2">
                        <Zap className="w-3 h-3 text-blue-500" />
                      </div>
                    )}
                    <span className="font-medium text-base">
                      {formattedTime}
                      {isCurrentHour && <span className="ml-1 text-xs text-blue-600 font-medium">(Nå)</span>}
                    </span>
                  </div>
                  
                  <div className="text-right">
                    <div className={`text-lg font-bold ${priceColorClass}`}>
                      {price.NOK_per_kWh.toFixed(2)} kr
                    </div>
                    <span className={`text-xs ${priceDiff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {priceDiff > 0 ? '+' : ''}{priceDiff.toFixed(0)}% fra snitt
                    </span>
                  </div>
                </div>
                
                {showSubsidy && subsidyInfo.isSubsidyActive && (
                  <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between items-center">
                    <span className="text-xs text-gray-500">Etter strømstøtte:</span>
                    <span className="text-base font-medium text-green-600">
                      {subsidyInfo.priceAfterSubsidy.toFixed(2)} kr
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* Desktop table layout */
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Tid
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    NOK/kWh
                  </th>
                  {showSubsidy && (
                    <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Etter støtte
                    </th>
                  )}
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    +/- Snitt
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedPrices.map((price, index) => {
                  const startTime = parseISO(price.time_start);
                  const endTime = parseISO(price.time_end);
                  const formattedTime = `${format(startTime, 'HH:mm', { locale: nb })} - ${format(endTime, 'HH:mm', { locale: nb })}`;
                  
                  const isCurrentHour = price.time_start === prices[currentHourIndex]?.time_start;
                  
                  // Calculate percentage difference from average
                  const priceDiff = ((price.NOK_per_kWh - avgPrice) / avgPrice) * 100;
                  
                  // Calculate subsidy
                  const subsidyInfo = calculateElectricitySubsidy(price.NOK_per_kWh, area);
                  
                  // Determine row styling
                  let rowClass = '';
                  if (isCurrentHour) {
                    rowClass = 'bg-blue-50';
                  } else if (index % 2 === 0) {
                    rowClass = 'bg-white';
                  } else {
                    rowClass = 'bg-gray-50';
                  }
                  
                  // Determine price color based on comparison to average
                  let priceColorClass = '';
                  if (price.NOK_per_kWh < avgPrice * 0.8) {
                    priceColorClass = 'text-green-700 font-medium';
                  } else if (price.NOK_per_kWh > avgPrice * 1.2) {
                    priceColorClass = 'text-red-700 font-medium';
                  }
                  
                  return (
                    <tr key={price.time_start} className={rowClass}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                        {isCurrentHour && (
                          <div className="bg-blue-100 p-1 rounded-full mr-2">
                            <Zap className="w-3 h-3 text-blue-500" />
                          </div>
                        )}
                        {formattedTime}
                        {isCurrentHour && <span className="ml-2 text-xs text-blue-600 font-medium">(Nå)</span>}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-medium ${priceColorClass}`}>
                        {price.NOK_per_kWh.toFixed(2)}
                      </td>
                      {showSubsidy && (
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                          {subsidyInfo.isSubsidyActive ? (
                            <span className="text-green-600 font-medium">{subsidyInfo.priceAfterSubsidy.toFixed(2)}</span>
                          ) : (
                            <span className="text-gray-500">{price.NOK_per_kWh.toFixed(2)}</span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        <span className={`
                          ${priceDiff > 0 ? 'text-red-600' : 'text-green-600'} 
                          ${Math.abs(priceDiff) > 15 ? 'font-medium' : ''}
                        `}>
                          {priceDiff > 0 ? '+' : ''}{priceDiff.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceList;